import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

export interface User {
  id: number;
  nombre: string;
  apellido: string;
  email: string;
  avatarUrl?: string | null;
}

interface LoginResponse {
  message: string;
  token: string;
  user: User;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private apiUrl = environment.apiUrl;

  private currentUserSubject = new BehaviorSubject<User | null>(this.getUserFromStorage());
  public currentUser$ = this.currentUserSubject.asObservable();
  public currentUser = signal<User | null>(this.getUserFromStorage());

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  login(credentials: { username: string; password: string; rememberMe?: boolean }): Observable<LoginResponse> {
    const payload = { username: credentials.username, password: credentials.password };
    return this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, payload).pipe(
      tap(response => {
        const storage = credentials.rememberMe ? localStorage : sessionStorage;
        storage.setItem('token', response.token);
        storage.setItem('user', JSON.stringify(response.user));
        this.currentUserSubject.next(response.user);
        this.currentUser.set(response.user);
      })
    );
  }

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    this.currentUserSubject.next(null);
    this.currentUser.set(null);
    this.router.navigate(['/auth/login']);
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp && Math.floor(Date.now() / 1000) >= payload.exp) {
        this.logout();
        return false;
      }
    } catch (e) {
      this.logout();
      return false;
    }

    return true;
  }

  updateAvatar(avatarUrl: string): void {
    const current = this.currentUserSubject.value;
    if (!current) return;

    const updated: User = { ...current, avatarUrl };

    // Actualizar en el mismo storage donde se guardó el usuario
    if (localStorage.getItem('user')) {
      localStorage.setItem('user', JSON.stringify(updated));
    } else if (sessionStorage.getItem('user')) {
      sessionStorage.setItem('user', JSON.stringify(updated));
    }

    this.currentUserSubject.next(updated);
    this.currentUser.set(updated);
  }

  private getUserFromStorage(): User | null {
    const user = localStorage.getItem('user') || sessionStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
}
